import { Box, Button, Heading, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { getAccountDetails } from "api/GoogleAuth";
import { refusalOf } from "api/refusal";
import { verifyEmail } from "api/SignInMethods";
import BusyButton from "components/UIComponents/BusyButton";
import PageLoader from "components/UIComponents/PageLoader";
import { RootState } from "redux/store";
import { SIGN_IN_PATH } from "utils/auth/signInPath";
import {
  clearVerifyEmailToken,
  takeVerifyEmailToken,
  VERIFY_EMAIL_PATH,
} from "utils/auth/verifyEmailToken";
import { FOCUS_RING, TOUCH_TARGET } from "utils/chackra/controlStyles";

const PROFILE_PATH = "/profile/info";
const ACTION_SIZE = { minH: TOUCH_TARGET, _focusVisible: FOCUS_RING };

/** An answer after which this token will never apply: drop it. */
type FinalOutcome = "done" | "expired" | "taken" | "alreadySet";
/** An answer the same token may still get past. `wrongAccount` is one: the
 *  server keeps the token for the account that asked, so after signing in as
 *  that account this page can still confirm it. */
type RetryableOutcome = "wrongAccount" | "mismatch" | "unavailable";
type Outcome = FinalOutcome | RetryableOutcome | "signedOut";

const FINAL_BY_DETAIL: Record<string, FinalOutcome> = {
  taken: "taken",
  email_already_set: "alreadySet",
};

const outcomeOf = (error: unknown): Outcome => {
  const { status, detail } = refusalOf(error);
  if (status === 401) return "signedOut";
  if (status === 400 && detail === "expired") return "expired";
  if (status === 409) {
    if (detail === "account_mismatch") return "mismatch";
    if (detail === "wrong_account") return "wrongAccount";
    if (typeof detail === "string" && FINAL_BY_DETAIL[detail]) return FINAL_BY_DETAIL[detail];
  }
  return "unavailable";
};

const isFinal = (outcome: Outcome): boolean =>
  !["signedOut", "wrongAccount", "mismatch", "unavailable"].includes(outcome);

const MESSAGE_ID: Record<Exclude<Outcome, "signedOut">, string> = {
  done: "verifyEmail.done",
  expired: "verifyEmail.expired",
  wrongAccount: "verifyEmail.wrongAccount",
  taken: "verifyEmail.taken",
  alreadySet: "verifyEmail.alreadySet",
  mismatch: "signInMethods.error.accountMismatch",
  unavailable: "signInMethods.error.unavailable",
};

/**
 * Where the letter's link lands (public route). Opening it changes nothing: a
 * mail scanner may fetch and even run this page. The email changes only when
 * the requester's own session presses Confirm.
 */
const VerifyEmailPage = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { isUserLoggedIn, sessionRestoreSettled, apiKey } = useSelector(
    (state: RootState) => state.userData
  );
  const [token] = useState<string | null>(takeVerifyEmailToken);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const confirmInFlight = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [answers, setAnswers] = useState(0);

  // Every answer can remove or disable the button that held focus.
  useEffect(() => {
    if (answers > 0) headingRef.current?.focus();
  }, [answers]);

  // Always the API's answer, never the store's: a sign-in whose profile fetch
  // failed stores a placeholder there, which would be a permanent mismatch.
  const resolveUserId = async (): Promise<number | null> => {
    if (!apiKey) return null;
    try {
      const details = await getAccountDetails(apiKey);
      const id = Number(details.data?.user_data?.user_id);
      return details.status === "success" && id > 0 ? id : null;
    } catch (error) {
      return null;
    }
  };

  const confirm = async () => {
    if (confirmInFlight.current || !token) return;
    confirmInFlight.current = true;
    setIsConfirming(true);
    let next: Outcome = "unavailable";
    const expectedUserId = await resolveUserId();
    if (expectedUserId !== null) {
      try {
        await verifyEmail(token, expectedUserId);
        next = "done";
      } catch (error) {
        next = outcomeOf(error);
      }
    }
    if (isFinal(next)) clearVerifyEmailToken();
    setOutcome(next);
    setIsConfirming(false);
    setAnswers((count) => count + 1);
    confirmInFlight.current = false;
  };

  if (token && !isUserLoggedIn && !sessionRestoreSettled) {
    return <PageLoader label={t("session.restoring")} />;
  }

  const shown: Outcome | null = !token
    ? "expired"
    : !isUserLoggedIn && outcome === null
    ? "signedOut"
    : outcome;

  const signInAction = (
    <Button
      as={RouterLink}
      to={SIGN_IN_PATH}
      state={{ from: { pathname: VERIFY_EMAIL_PATH } }}
      colorScheme="blue"
      {...ACTION_SIZE}
    >
      {t("verifyEmail.signInAction")}
    </Button>
  );

  const profileAction = (
    <Button as={RouterLink} to={PROFILE_PATH} colorScheme="blue" {...ACTION_SIZE}>
      {t("verifyEmail.toProfile")}
    </Button>
  );

  let body;
  if (shown === "signedOut") {
    body = (
      <>
        <Text role="status" mb={6}>
          {t("verifyEmail.signIn")}
        </Text>
        {signInAction}
      </>
    );
  } else if (shown === null || shown === "mismatch" || shown === "unavailable") {
    body = (
      <>
        {shown ? (
          <Text role="alert" mb={6} color="status.errorStrong">
            {t(MESSAGE_ID[shown])}
          </Text>
        ) : (
          <Text mb={6}>{t("verifyEmail.prompt")}</Text>
        )}
        <BusyButton isBusy={isConfirming} onClick={confirm} {...ACTION_SIZE}>
          {t("verifyEmail.confirm")}
        </BusyButton>
      </>
    );
  } else if (shown === "done") {
    body = (
      <>
        <Text role="status" mb={6} color="status.successStrong">
          {t(MESSAGE_ID.done)}
        </Text>
        {profileAction}
      </>
    );
  } else {
    body = (
      <>
        <Text role="alert" mb={6} color="status.errorStrong">
          {t(MESSAGE_ID[shown])}
        </Text>
        {shown === "wrongAccount" ? signInAction : profileAction}
      </>
    );
  }

  return (
    <Box
      as="main"
      maxW="32rem"
      mx="auto"
      my={{ base: 6, md: 12 }}
      p={{ base: 4, md: 6 }}
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="lg"
      color="text.primary"
    >
      <Heading
        as="h1"
        ref={headingRef}
        tabIndex={-1}
        fontSize="2xl"
        fontWeight="semibold"
        mb={4}
        _focusVisible={FOCUS_RING}
      >
        {t("verifyEmail.title")}
      </Heading>
      {body}
    </Box>
  );
};

export default VerifyEmailPage;
